import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AlertMessage from 'components/UI/V3/AlertMessage/AlertMessage';
import clsx from 'clsx';
import { FunctionComponent, PointerEvent, useRef, useState } from 'react';
import styles from './BrandColorPicker.module.scss';

const SUGGESTED_COLORS = [
    '#EAB464',
    '#C18733',
    '#F2D2A2',
    '#5E8677',
    '#92B4A7',
    '#BDD2CA',
    '#BAA997',
    '#DCCCBB',
    '#BCB8AE',
    '#DBDAD7'
];

interface Hsv {
    h: number;
    s: number;
    v: number;
}

const hexToHsv = (hex: string): Hsv => {
    const r = parseInt(hex.substring(1, 3), 16) / 255;
    const g = parseInt(hex.substring(3, 5), 16) / 255;
    const b = parseInt(hex.substring(5, 7), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const delta = max - min;

    let h = 0;
    if (delta !== 0) {
        if (max === r) h = ((g - b) / delta) % 6;
        else if (max === g) h = (b - r) / delta + 2;
        else h = (r - g) / delta + 4;
    }
    h = Math.round(h * 60);
    if (h < 0) h += 360;

    return { h, s: max === 0 ? 0 : delta / max, v: max };
};

const hsvToHex = ({ h, s, v }: Hsv): string => {
    const c = v * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = v - c;

    let rgb: [number, number, number];
    if (h < 60) rgb = [c, x, 0];
    else if (h < 120) rgb = [x, c, 0];
    else if (h < 180) rgb = [0, c, x];
    else if (h < 240) rgb = [0, x, c];
    else if (h < 300) rgb = [x, 0, c];
    else rgb = [c, 0, x];

    const toHex = (value: number) =>
        Math.round((value + m) * 255)
            .toString(16)
            .padStart(2, '0');

    return `#${toHex(rgb[0])}${toHex(rgb[1])}${toHex(rgb[2])}`.toUpperCase();
};

interface BrandColorPickerProps {
    color: string;
    variant?: 'popover' | 'drawer';
    onChange: (color: string) => void;
    onClose: () => void;
}

const BrandColorPicker: FunctionComponent<BrandColorPickerProps> = props => {
    const svAreaRef = useRef<HTMLDivElement>(null);
    const hueSliderRef = useRef<HTMLDivElement>(null);

    const [hsv, setHsv] = useState<Hsv>(() => hexToHsv(props.color));

    const updateHsv = (next: Hsv) => {
        setHsv(next);
        props.onChange(hsvToHex(next));
    };

    const svHandler = (event: PointerEvent<HTMLDivElement>) => {
        const rect = svAreaRef.current!.getBoundingClientRect();
        const s = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
        const v = 1 - Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height));
        updateHsv({ ...hsv, s, v });
    };

    const hueHandler = (event: PointerEvent<HTMLDivElement>) => {
        const rect = hueSliderRef.current!.getBoundingClientRect();
        const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
        updateHsv({ ...hsv, h: Math.round(ratio * 359) });
    };

    const dragHandler =
        (moveHandler: (event: PointerEvent<HTMLDivElement>) => void) => (event: PointerEvent<HTMLDivElement>) => {
            event.currentTarget.setPointerCapture(event.pointerId);
            moveHandler(event);
        };

    const dragMoveHandler =
        (moveHandler: (event: PointerEvent<HTMLDivElement>) => void) => (event: PointerEvent<HTMLDivElement>) => {
            if (event.buttons === 1) moveHandler(event);
        };

    const selectSuggestedHandler = (color: string) => {
        setHsv(hexToHsv(color));
        props.onChange(color);
    };

    return (
        <div className={props.variant === 'drawer' ? styles.drawer : styles.container}>
            <div className={styles.pickerColumn}>
                <p className={styles.title}>Select brand color</p>
                <div
                    ref={svAreaRef}
                    className={styles.svArea}
                    style={{
                        backgroundImage: `linear-gradient(180deg, transparent, #000), linear-gradient(90deg, #fff, hsl(${hsv.h}, 100%, 50%))`
                    }}
                    onPointerDown={dragHandler(svHandler)}
                    onPointerMove={dragMoveHandler(svHandler)}
                >
                    <div
                        className={styles.thumb}
                        style={{ left: `${hsv.s * 100}%`, top: `${(1 - hsv.v) * 100}%` }}
                    />
                </div>
                <div className={styles.sliders}>
                    <div
                        ref={hueSliderRef}
                        className={styles.hueSlider}
                        onPointerDown={dragHandler(hueHandler)}
                        onPointerMove={dragMoveHandler(hueHandler)}
                    >
                        <div
                            className={styles.thumb}
                            style={{ left: `${(hsv.h / 359) * 100}%`, top: '50%' }}
                        />
                    </div>
                </div>
            </div>
            <div className={styles.suggestedColumn}>
                <div>
                    <p className={styles.suggestedTitle}>Suggested colors</p>
                    <p className={styles.suggestedDescription}>These colors are optimized for readability.</p>
                </div>
                <div className={styles.swatchGrid}>
                    {SUGGESTED_COLORS.map(color => (
                        <button
                            key={color}
                            className={clsx(styles.swatch, {
                                [styles.swatchSelected]: color === props.color.toUpperCase()
                            })}
                            style={{ backgroundColor: color }}
                            aria-label={color}
                            onClick={() => selectSuggestedHandler(color)}
                        />
                    ))}
                </div>
                <div className={styles.alert}>
                    <AlertMessage
                        icon={faInfoCircle}
                        iconColor="#BAA997"
                        bdColor="#BCB8AE"
                        bgColor="#F8F5F1"
                    >
                        <span className={styles.alertText}>Very light colors can be hard to read.</span>
                    </AlertMessage>
                </div>
            </div>
            <button
                className={styles.closeButton}
                aria-label="Close color picker"
                onClick={props.onClose}
            >
                <FontAwesomeIcon icon={faTimes} />
            </button>
        </div>
    );
};

export default BrandColorPicker;
